import * as React from 'react';
import {
  buildAvailabilityProposal,
  buildAvailabilityUpdate,
  createAvailabilityDraft,
  createWaitlistDraft,
  findNextWaitlistEntry,
  findTourToConfirm,
  findWaitlistPlacement,
  getClarifyingQuestion,
  getNextMissingField,
  interpretAvailabilityMessage,
  interpretHoursNotice,
  interpretWaitlistMessage,
  getWaitlistQuestion,
} from '../assistant';
import { api } from '../api';
import { AGE_GROUPS } from '../data';
import { formatAvailabilityFreshness } from '../freshness';
import { useApi, useAsyncAction } from '../hooks';
import { Icon } from '../icons.jsx';
import { Badge, Button } from '../ui.jsx';

const PROVIDER_ID = 'sunnyside';
const EXAMPLE_MESSAGE = 'We have 3 openings for Pre-K';
const POST_EXAMPLE_MESSAGE = 'Post this picture with a nice caption';
const TOUR_EXAMPLE_MESSAGE = 'Confirm Maya’s tour';
const WAITLIST_EXAMPLE_MESSAGE = 'Add the Williams family to the infant waitlist';
const HOURS_EXAMPLE_MESSAGE = 'We’re closing at 4 on Friday';
const CONTACT_WAITLIST_EXAMPLE_MESSAGE = 'Contact the next infant family';
const EXAMPLE_POST = {
  image: '/assets/image8.webp',
  caption:
    'Fresh air, curious hands, and room to explore. Our garden gives children a place to notice the seasons, care for growing things, and learn together beyond the classroom.',
  altText: 'Children exploring plants in Sunnyside Learning Garden’s outdoor garden.',
};

const initialMessages = [
  {
    id: 'welcome',
    role: 'assistant',
    text: 'Hi Lena. This in-app chat gives you a richer place to manage Sunnyside while you run the center.',
  },
  {
    id: 'ready',
    role: 'assistant',
    text: 'Tell me what changed, attach a photo, or review a family request. I’ll show the exact action before anything goes live.',
  },
];

const Message = ({ message }) => (
  <div className={`assistant-message-row is-${message.role}`}>
    {message.role === 'assistant' && (
      <span className="assistant-avatar" aria-hidden="true">
        <Icon name="spark" size={16}/>
      </span>
    )}
    <div className={`assistant-message is-${message.role}`}>
      {message.image && (
        <img className="assistant-message-image" src={message.image} alt="Attached provider photo"/>
      )}
      <span>{message.text}</span>
      <time>{message.time || 'Now'}</time>
    </div>
  </div>
);

const QuickReplies = ({ missingField, onReply }) => {
  if (!missingField) return null;

  const options = {
    ageGroup: ['Infant', 'Young toddler', 'Older toddler', 'Pre-K', 'School age'],
    count: ['1 opening', '2 openings', '3 openings'],
    schedule: ['Full-time', 'Part-time', 'Both'],
    startDate: ['Available now', 'Starting October 15'],
  }[missingField];

  return (
    <div className="assistant-quick-replies" aria-label="Suggested replies">
      {options.map((option) => (
        <button key={option} type="button" onClick={() => onReply(option)}>
          {option}
        </button>
      ))}
    </div>
  );
};

const AvailabilityContext = ({ provider, bookings, waitlistEntries, notices }) => {
  const pendingTours = bookings?.filter((booking) => booking.status === 'pending') || [];
  const waitingFamilies =
    waitlistEntries?.filter((entry) => entry.status === 'waiting') || [];
  return (
  <aside className="assistant-context" aria-label="Current center availability">
    <div className="assistant-context-heading">
      <div>
        <span className="assistant-eyebrow">Current public record</span>
        <h2>Sunnyside Learning Garden</h2>
        <small className="assistant-freshness">
          Confirmed {formatAvailabilityFreshness(provider?.availabilityUpdatedAt)}
        </small>
      </div>
      <Badge tone="open" icon="live">Live</Badge>
    </div>

    {pendingTours.length > 0 && (
      <div className="assistant-tour-alert">
        <span>
          <Icon name="calendar-check" size={17}/>
        </span>
        <div>
          <strong>
            {pendingTours.length} tour {pendingTours.length === 1 ? 'request' : 'requests'} waiting
          </strong>
          <small>
            {pendingTours[0].parentName} · {pendingTours[0].date} at {pendingTours[0].time}
          </small>
        </div>
      </div>
    )}
    {waitingFamilies.length > 0 && (
      <div className="assistant-tour-alert">
        <span><Icon name="inbox" size={17}/></span>
        <div>
          <strong>{waitingFamilies.length} families waiting</strong>
          <small>Across all age groups at this center</small>
        </div>
      </div>
    )}
    {notices?.length > 0 && (
      <div className="assistant-tour-alert">
        <span><Icon name="bell" size={17}/></span>
        <div>
          <strong>{notices[0].message}</strong>
          <small>{notices[0].dateLabel} · visible on the public profile</small>
        </div>
      </div>
    )}

    <div className="assistant-availability-list">
      {AGE_GROUPS.map((group) => {
        const slot = provider?.availability[group];
        const isOpen = (slot?.spots || 0) > 0;
        return (
          <div key={group} className="assistant-availability-row">
            <div>
              <strong>{group}</strong>
              <span>{slot?.note || 'Loading current status'}</span>
            </div>
            <span className={isOpen ? 'is-open' : 'is-closed'}>
              {isOpen ? `${slot.spots} open` : 'Waitlist'}
            </span>
          </div>
        );
      })}
    </div>

    <div className="assistant-safety-note">
      <Icon name="shield" size={17}/>
      <div>
        <strong>You stay in control</strong>
        <span>Nothing public changes until you approve the exact update.</span>
      </div>
    </div>
  </aside>
  );
};

export const AssistantScreen = ({ go }) => {
  const [messages, setMessages] = React.useState(initialMessages);
  const [draft, setDraft] = React.useState(createAvailabilityDraft);
  const [input, setInput] = React.useState('');
  const [proposal, setProposal] = React.useState(null);
  const [published, setPublished] = React.useState(null);
  const [postDraft, setPostDraft] = React.useState(null);
  const [publishedPost, setPublishedPost] = React.useState(null);
  const [tourProposal, setTourProposal] = React.useState(null);
  const [confirmedTour, setConfirmedTour] = React.useState(null);
  const [waitlistDraft, setWaitlistDraft] = React.useState(createWaitlistDraft);
  const [waitlistProposal, setWaitlistProposal] = React.useState(null);
  const [addedWaitlistEntry, setAddedWaitlistEntry] = React.useState(null);
  const [freshnessConfirmed, setFreshnessConfirmed] = React.useState(false);
  const [noticeProposal, setNoticeProposal] = React.useState(null);
  const [publishedNotice, setPublishedNotice] = React.useState(null);
  const [waitlistContactProposal, setWaitlistContactProposal] = React.useState(null);
  const [waitlistContactMessage, setWaitlistContactMessage] = React.useState('');
  const [contactedWaitlistEntry, setContactedWaitlistEntry] = React.useState(null);
  const [placementProposal, setPlacementProposal] = React.useState(null);
  const [placedWaitlistEntry, setPlacedWaitlistEntry] = React.useState(null);
  const [photoConsent, setPhotoConsent] = React.useState(false);
  const [attachmentError, setAttachmentError] = React.useState('');
  const [showAllExamples, setShowAllExamples] = React.useState(false);
  const messageId = React.useRef(0);
  const transcriptEnd = React.useRef(null);
  const fileInput = React.useRef(null);

  const {
    data: provider,
    loading,
    error,
    reload,
  } = useApi(() => api.providers.get(PROVIDER_ID), [PROVIDER_ID]);
  const {
    data: bookings,
    loading: bookingsLoading,
    error: bookingsError,
    reload: reloadBookings,
  } = useApi(() => api.bookings.list(PROVIDER_ID), [PROVIDER_ID]);
  const {
    data: waitlistEntries,
    loading: waitlistsLoading,
    error: waitlistsError,
    reload: reloadWaitlists,
  } = useApi(() => api.waitlists.list(PROVIDER_ID), [PROVIDER_ID]);
  const {
    data: notices,
    error: noticesError,
    reload: reloadNotices,
  } = useApi(() => api.notices.list(PROVIDER_ID), [PROVIDER_ID]);

  const publishAction = useAsyncAction(async (nextProposal) => {
    const result = await api.providers.updateAvailability(
      PROVIDER_ID,
      buildAvailabilityUpdate(nextProposal),
    );
    return result;
  });
  const publishPostAction = useAsyncAction((nextPost) => api.posts.create(nextPost));
  const confirmTourAction = useAsyncAction((bookingId) =>
    api.bookings.confirm(PROVIDER_ID, bookingId),
  );
  const addWaitlistAction = useAsyncAction((entry) => api.waitlists.create(entry));
  const refreshAvailabilityAction = useAsyncAction(() =>
    api.providers.updateAvailability(PROVIDER_ID, {}),
  );
  const publishNoticeAction = useAsyncAction((notice) => api.notices.create(notice));
  const contactWaitlistAction = useAsyncAction((entryId) =>
    api.waitlists.contact(PROVIDER_ID, entryId),
  );
  const placeWaitlistAction = useAsyncAction((entryId) =>
    api.waitlists.place(PROVIDER_ID, entryId),
  );

  React.useEffect(() => {
    transcriptEnd.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
  }, [
    messages,
    proposal,
    published,
    postDraft,
    publishedPost,
    tourProposal,
    confirmedTour,
    waitlistProposal,
    addedWaitlistEntry,
    freshnessConfirmed,
    noticeProposal,
    publishedNotice,
    waitlistContactProposal,
    contactedWaitlistEntry,
    placementProposal,
    placedWaitlistEntry,
  ]);

  const appendMessages = (...nextMessages) => {
    setMessages((current) => [
      ...current,
      ...nextMessages.map((message) => ({
        id: `message-${messageId.current++}`,
        time: 'Now',
        ...message,
      })),
    ]);
  };

  const submitMessage = (rawMessage) => {
    const text = rawMessage.trim();
    if (
      !text ||
      !provider ||
      publishAction.loading ||
      publishPostAction.loading ||
      confirmTourAction.loading ||
      addWaitlistAction.loading
      || publishNoticeAction.loading ||
      contactWaitlistAction.loading ||
      placeWaitlistAction.loading
    ) return;

    const placement = findWaitlistPlacement(text, waitlistEntries || []);
    if (placement) {
      setInput('');
      setProposal(null);
      setPostDraft(null);
      setTourProposal(null);
      setWaitlistProposal(null);
      setNoticeProposal(null);
      setWaitlistContactProposal(null);
      setPlacedWaitlistEntry(null);
      setPlacementProposal(placement);
      appendMessages(
        { role: 'user', text },
        {
          role: 'assistant',
          text: `I found ${placement.parentName}. Review the placement before removing them from the active waitlist.`,
        },
      );
      return;
    }

    const nextWaitlistEntry = findNextWaitlistEntry(text, waitlistEntries || []);
    if (nextWaitlistEntry) {
      const ageArticle = nextWaitlistEntry.childAge === 'Infant' ? 'an' : 'a';
      const contactMessage = `Hi ${nextWaitlistEntry.parentName}, Sunnyside Learning Garden may have ${ageArticle} ${nextWaitlistEntry.childAge} opening. Reply YES if you would like the center to contact you about next steps.`;
      setInput('');
      setProposal(null);
      setPostDraft(null);
      setTourProposal(null);
      setWaitlistProposal(null);
      setNoticeProposal(null);
      setContactedWaitlistEntry(null);
      setWaitlistContactProposal(nextWaitlistEntry);
      setWaitlistContactMessage(contactMessage);
      appendMessages(
        { role: 'user', text },
        {
          role: 'assistant',
          text: `I found the next ${nextWaitlistEntry.childAge} family. Review the message before contacting them.`,
        },
      );
      return;
    }

    if (
      /\b(?:contact|text|message|notify)\b/i.test(text) &&
      /\b(?:waitlist|family)\b/i.test(text)
    ) {
      setInput('');
      appendMessages(
        { role: 'user', text },
        {
          role: 'assistant',
          text: 'I couldn’t find a waiting family that matches that request.',
        },
      );
      return;
    }

    const hoursNotice = interpretHoursNotice(text);
    if (hoursNotice) {
      setInput('');
      setProposal(null);
      setPostDraft(null);
      setTourProposal(null);
      setWaitlistProposal(null);
      setNoticeProposal(hoursNotice);
      setPublishedNotice(null);
      appendMessages(
        { role: 'user', text },
        {
          role: 'assistant',
          text: 'I turned that into a special-hours notice. Review the exact date and time below.',
        },
      );
      return;
    }

    const nextWaitlistDraft = interpretWaitlistMessage(text, waitlistDraft);
    if (nextWaitlistDraft) {
      setWaitlistDraft(nextWaitlistDraft);
      setInput('');
      setProposal(null);
      setPostDraft(null);
      setTourProposal(null);
      setAddedWaitlistEntry(null);
      const question = getWaitlistQuestion(nextWaitlistDraft);
      appendMessages(
        { role: 'user', text },
        {
          role: 'assistant',
          text: question || 'I have everything I need. Review the waitlist entry below.',
        },
      );
      if (!question) setWaitlistProposal(nextWaitlistDraft);
      return;
    }

    const matchingTour = findTourToConfirm(text, bookings || []);
    if (matchingTour) {
      setProposal(null);
      setPostDraft(null);
      setPublished(null);
      setPublishedPost(null);
      setConfirmedTour(null);
      setWaitlistProposal(null);
      setTourProposal(matchingTour);
      setInput('');
      appendMessages(
        { role: 'user', text },
        {
          role: 'assistant',
          text: `I found ${matchingTour.parentName}’s ${matchingTour.tourType === 'virtual' ? 'virtual' : 'in-person'} tour request. Review it below before confirming.`,
        },
      );
      return;
    }

    if (/\bconfirm\b/i.test(text) && /\b(?:tour|visit|appointment)\b/i.test(text)) {
      setInput('');
      appendMessages(
        { role: 'user', text },
        {
          role: 'assistant',
          text: 'I couldn’t find one pending tour that matches. Open the app to review all tour requests.',
        },
      );
      return;
    }

    const nextDraft = interpretAvailabilityMessage(text, draft);
    const missingField = getNextMissingField(nextDraft);
    setDraft(nextDraft);
    setInput('');
    setPublished(null);
    setPublishedPost(null);
    setTourProposal(null);
    setConfirmedTour(null);
    setWaitlistProposal(null);

    if (missingField) {
      setProposal(null);
      appendMessages(
        { role: 'user', text },
        { role: 'assistant', text: getClarifyingQuestion(missingField) },
      );
      return;
    }

    const nextProposal = buildAvailabilityProposal(
      nextDraft,
      provider.availability[nextDraft.ageGroup],
    );
    setProposal(nextProposal);
    appendMessages(
      { role: 'user', text },
      {
        role: 'assistant',
        text: 'I have everything I need. Review the exact change below before it goes live.',
      },
    );
  };

  const startPostDraft = (draft) => {
    setProposal(null);
    setPublished(null);
    setPublishedPost(null);
    setTourProposal(null);
    setConfirmedTour(null);
    setPostDraft(draft);
    setPhotoConsent(false);
    setAttachmentError('');
    appendMessages(
      { role: 'user', text: POST_EXAMPLE_MESSAGE, image: draft.image },
      {
        role: 'assistant',
        text: 'I drafted a profile update and image description. Review both, confirm photo permission, and publish when it feels right.',
      },
    );
  };

  const handleAttachment = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setAttachmentError('Choose an image file to create a profile post.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setAttachmentError('Choose an image smaller than 10 MB.');
      return;
    }
    const image = URL.createObjectURL(file);
    startPostDraft({
      image,
      caption:
        'A moment from our day at Sunnyside Learning Garden. We love creating spaces where children can explore, learn, and grow together.',
      altText: 'A recent activity at Sunnyside Learning Garden.',
    });
  };

  const publishProposal = async () => {
    if (!proposal) return;
    try {
      const result = await publishAction.run(proposal);
      setPublished({ proposal, result });
      setProposal(null);
      setDraft(createAvailabilityDraft());
      appendMessages({
        role: 'assistant',
        text: `Published. Families can now see ${proposal.nextCount} ${proposal.ageGroup} opening${proposal.nextCount === 1 ? '' : 's'}.`,
      });
      reload();
    } catch {
      // useAsyncAction exposes the error in the review panel.
    }
  };

  const editProposal = () => {
    setProposal(null);
    appendMessages({
      role: 'assistant',
      text: 'What should I change? You can reply with a new count, schedule, or start date.',
    });
  };

  const cancelProposal = () => {
    setProposal(null);
    setDraft(createAvailabilityDraft());
    appendMessages({
      role: 'assistant',
      text: 'Canceled. Your public availability was not changed.',
    });
  };

  const publishPost = async () => {
    if (!postDraft || !photoConsent) return;
    try {
      const post = await publishPostAction.run({
        providerId: PROVIDER_ID,
        ...postDraft,
      });
      setPublishedPost(post);
      setPostDraft(null);
      setPhotoConsent(false);
      appendMessages({
        role: 'assistant',
        text: 'Published. The photo and caption are now live on Sunnyside’s profile.',
      });
    } catch {
      // useAsyncAction exposes the error in the review panel.
    }
  };

  const cancelPost = () => {
    setPostDraft(null);
    setPhotoConsent(false);
    appendMessages({
      role: 'assistant',
      text: 'Canceled. The photo was not added to your public profile.',
    });
  };

  const confirmTour = async () => {
    if (!tourProposal) return;
    try {
      const booking = await confirmTourAction.run(tourProposal.id);
      setConfirmedTour(booking);
      setTourProposal(null);
      appendMessages({
        role: 'assistant',
        text: `Confirmed. ${booking.parentName} will receive the ${booking.tourType === 'virtual' ? 'virtual' : 'in-person'} tour details.`,
      });
      reloadBookings();
    } catch {
      // useAsyncAction exposes the error in the review panel.
    }
  };

  const cancelTourConfirmation = () => {
    setTourProposal(null);
    appendMessages({
      role: 'assistant',
      text: 'Canceled. The tour request is still waiting for a response.',
    });
  };

  const addToWaitlist = async () => {
    if (!waitlistProposal) return;
    try {
      const entry = await addWaitlistAction.run({
        providerId: PROVIDER_ID,
        parentName: waitlistProposal.parentName,
        childAge: waitlistProposal.childAge,
        phone: waitlistProposal.phone,
      });
      setAddedWaitlistEntry(entry);
      setWaitlistProposal(null);
      setWaitlistDraft(createWaitlistDraft());
      appendMessages({
        role: 'assistant',
        text: `Added ${entry.parentName} to the ${entry.childAge} waitlist.`,
      });
      reloadWaitlists();
    } catch {
      // useAsyncAction exposes the error in the review panel.
    }
  };

  const cancelWaitlist = () => {
    setWaitlistProposal(null);
    setWaitlistDraft(createWaitlistDraft());
    appendMessages({
      role: 'assistant',
      text: 'Canceled. The family was not added to the waitlist.',
    });
  };

  const confirmCurrentAvailability = async () => {
    try {
      await refreshAvailabilityAction.run();
      setFreshnessConfirmed(true);
      appendMessages(
        { role: 'user', text: 'Yes, keep it live' },
        {
          role: 'assistant',
          text: 'Confirmed. Families can see that Sunnyside’s availability was checked just now.',
        },
      );
      reload();
    } catch {
      // useAsyncAction exposes the error in the check-in card.
    }
  };

  const startAvailabilityReview = () => {
    appendMessages({
      role: 'assistant',
      text: 'Tell me which age group changed and how many openings families should see.',
    });
  };

  const publishNotice = async () => {
    if (!noticeProposal) return;
    try {
      const notice = await publishNoticeAction.run({
        providerId: PROVIDER_ID,
        ...noticeProposal,
      });
      setPublishedNotice(notice);
      setNoticeProposal(null);
      appendMessages({
        role: 'assistant',
        text: `Published. Families will see that Sunnyside is ${notice.message.toLowerCase()} on ${notice.dateLabel}.`,
      });
      reloadNotices();
    } catch {
      // useAsyncAction exposes the error in the review panel.
    }
  };

  const cancelNotice = () => {
    setNoticeProposal(null);
    appendMessages({
      role: 'assistant',
      text: 'Canceled. The center’s public hours were not changed.',
    });
  };

  const contactWaitlistFamily = async () => {
    if (!waitlistContactProposal) return;
    try {
      const entry = await contactWaitlistAction.run(waitlistContactProposal.id);
      setContactedWaitlistEntry(entry);
      setWaitlistContactProposal(null);
      appendMessages({
        role: 'assistant',
        text: `Marked ${entry.parentName} as contacted. The family text is simulated in this prototype.`,
      });
      reloadWaitlists();
    } catch {
      // useAsyncAction exposes the error in the review panel.
    }
  };

  const cancelWaitlistContact = () => {
    setWaitlistContactProposal(null);
    appendMessages({
      role: 'assistant',
      text: 'Canceled. The family was not contacted.',
    });
  };

  const placeWaitlistFamily = async () => {
    if (!placementProposal) return;
    try {
      const entry = await placeWaitlistAction.run(placementProposal.id);
      setPlacedWaitlistEntry(entry);
      setPlacementProposal(null);
      appendMessages({
        role: 'assistant',
        text: `Placed ${entry.parentName}. They are no longer on the active ${entry.childAge} waitlist.`,
      });
      reloadWaitlists();
    } catch {
      // useAsyncAction exposes the error in the review panel.
    }
  };

  const cancelPlacement = () => {
    setPlacementProposal(null);
    appendMessages({
      role: 'assistant',
      text: 'Canceled. The family remains marked as contacted.',
    });
  };

  const missingField =
    proposal ||
    postDraft ||
    tourProposal ||
    waitlistDraft.parentName ||
    noticeProposal ||
    waitlistContactProposal ||
    placementProposal
      ? null
      : getNextMissingField(draft);
  const canSend =
    input.trim().length > 0 &&
    !loading &&
    !publishAction.loading &&
    !publishPostAction.loading &&
    !confirmTourAction.loading &&
    !addWaitlistAction.loading &&
    !publishNoticeAction.loading &&
    !contactWaitlistAction.loading &&
    !placeWaitlistAction.loading;

  return (
    <div className="assistant-page route-in">
      <header className="assistant-topbar">
        <button
          className="assistant-brand"
          type="button"
          aria-label="Find My Childcare home"
          onClick={() => go('home')}
        >
          <img src="/assets/logo.png" alt=""/>
          <span>Find My Childcare</span>
        </button>
        <div className="assistant-topbar-actions">
          <span className="assistant-prototype-badge">
            <Badge tone="yellow">Prototype</Badge>
          </span>
          <Button variant="secondary" size="sm" icon="grid" onClick={() => go('dashboard')}>
            Dashboard
          </Button>
          <Button variant="ghost" size="sm" icon="phone" onClick={() => go('textdemo')}>
            See SMS mock
          </Button>
        </div>
      </header>

      <main className="assistant-workspace">
        <section className="assistant-conversation" aria-labelledby="assistant-title">
          <div className="assistant-conversation-header">
            <div className="assistant-heading-lockup">
              <span className="assistant-large-avatar" aria-hidden="true">
                <Icon name="spark" size={21}/>
              </span>
              <div>
                <span className="assistant-eyebrow">In-app provider chat</span>
                <h1 id="assistant-title">Manage your center in a guided chat</h1>
                <p>Richer reviews and editing for Sunnyside Learning Garden</p>
              </div>
            </div>
            <span className="assistant-status">
              <span className="live-dot" aria-hidden="true"/>
              Ready
            </span>
          </div>

          {error && (
            <div className="assistant-error" role="alert">
              We couldn’t load Sunnyside’s current availability: {error.message}
              <button type="button" onClick={reload}>Try again</button>
            </div>
          )}
          {bookingsError && (
            <div className="assistant-error" role="alert">
              We couldn’t load the latest tour requests: {bookingsError.message}
              <button type="button" onClick={reloadBookings}>Try again</button>
            </div>
          )}
          {waitlistsError && (
            <div className="assistant-error" role="alert">
              We couldn’t load the current waitlist: {waitlistsError.message}
              <button type="button" onClick={reloadWaitlists}>Try again</button>
            </div>
          )}
          {noticesError && (
            <div className="assistant-error" role="alert">
              We couldn’t load special-hours notices: {noticesError.message}
              <button type="button" onClick={reloadNotices}>Try again</button>
            </div>
          )}

          <div className="assistant-transcript" aria-live="polite">
            <div className="assistant-day-label">Today</div>
            {messages.map((message) => <Message key={message.id} message={message}/>)}

            {messages.length === initialMessages.length && (
              <>
                <section className="assistant-checkin" aria-label="Weekly availability check-in">
                  <div>
                    <span className="assistant-eyebrow">Weekly check-in</span>
                    <strong>Are these openings still correct?</strong>
                    <p>
                      {AGE_GROUPS.filter((group) => provider?.availability[group].spots > 0)
                        .map((group) => `${group}: ${provider.availability[group].spots}`)
                        .join(' · ')}
                    </p>
                  </div>
                  <div>
                    <Button
                      variant="primary"
                      size="sm"
                      icon="check"
                      onClick={confirmCurrentAvailability}
                      disabled={refreshAvailabilityAction.loading}
                    >
                      {refreshAvailabilityAction.loading ? 'Confirming…' : 'Yes, keep it live'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={startAvailabilityReview}>
                      I need changes
                    </Button>
                  </div>
                  {refreshAvailabilityAction.error && (
                    <p className="assistant-review-error" role="alert">
                      Couldn’t refresh availability: {refreshAvailabilityAction.error.message}
                    </p>
                  )}
                </section>
                <div className="assistant-examples">
                <button
                  className="assistant-example"
                  type="button"
                  onClick={() => submitMessage(EXAMPLE_MESSAGE)}
                  disabled={loading}
                >
                  <span>Update availability</span>
                  “{EXAMPLE_MESSAGE}”
                  <Icon name="arrow-right" size={15}/>
                </button>
                <button
                  className="assistant-example"
                  type="button"
                  onClick={() => startPostDraft(EXAMPLE_POST)}
                  disabled={loading}
                >
                  <span>Publish a photo</span>
                  “{POST_EXAMPLE_MESSAGE}”
                  <Icon name="arrow-right" size={15}/>
                </button>
                <button
                  className="assistant-example"
                  type="button"
                  onClick={() => submitMessage(TOUR_EXAMPLE_MESSAGE)}
                  disabled={bookingsLoading || !bookings?.some((booking) => booking.status === 'pending')}
                >
                  <span>Confirm a tour</span>
                  “{TOUR_EXAMPLE_MESSAGE}”
                  <Icon name="arrow-right" size={15}/>
                </button>
                {showAllExamples && (
                  <>
                    <button
                      className="assistant-example"
                      type="button"
                      onClick={() => submitMessage(WAITLIST_EXAMPLE_MESSAGE)}
                      disabled={waitlistsLoading}
                    >
                      <span>Add to waitlist</span>
                      “Add the Williams family”
                      <Icon name="arrow-right" size={15}/>
                    </button>
                  <button
                    className="assistant-example"
                    type="button"
                    onClick={() => submitMessage(HOURS_EXAMPLE_MESSAGE)}
                  >
                    <span>Change hours</span>
                    “Closing at 4 on Friday”
                    <Icon name="arrow-right" size={15}/>
                  </button>
                  <button
                    className="assistant-example"
                    type="button"
                    onClick={() => submitMessage(CONTACT_WAITLIST_EXAMPLE_MESSAGE)}
                    disabled={
                      !waitlistEntries?.some(
                        (entry) => entry.status === 'waiting' && entry.childAge === 'Infant',
                      )
                    }
                  >
                    <span>Contact next family</span>
                    “Next infant family”
                    <Icon name="arrow-right" size={15}/>
                  </button>
                  </>
                )}
                <button
                  className="assistant-more-actions"
                  type="button"
                  onClick={() => setShowAllExamples(value => !value)}
                >
                  <Icon name={showAllExamples ? 'chev-left' : 'plus'} size={14}/>
                  {showAllExamples ? 'Show fewer actions' : 'More actions'}
                </button>
                </div>
              </>
            )}

            {proposal && (
              <section className="assistant-review" aria-label="Availability update review">
                <div className="assistant-review-heading">
                  <span>
                    <Icon name="edit" size={16}/>
                  </span>
                  <div>
                    <strong>Review availability update</strong>
                    <p>This is the exact change families will see.</p>
                  </div>
                </div>

                <dl>
                  <div>
                    <dt>Age group</dt>
                    <dd>{proposal.ageGroup}</dd>
                  </div>
                  <div>
                    <dt>Openings</dt>
                    <dd>
                      <span className="assistant-previous-value">{proposal.previousCount}</span>
                      <Icon name="arrow-right" size={14}/>
                      <strong>{proposal.nextCount}</strong>
                    </dd>
                  </div>
                  <div>
                    <dt>Schedule</dt>
                    <dd>{proposal.schedule}</dd>
                  </div>
                  <div>
                    <dt>Starts</dt>
                    <dd>{proposal.startDate.replace(/^Starts\s+/i, '')}</dd>
                  </div>
                </dl>

                {publishAction.error && (
                  <p className="assistant-review-error" role="alert">
                    Couldn’t publish this update: {publishAction.error.message}
                  </p>
                )}

                <div className="assistant-review-actions">
                  <Button
                    variant="primary"
                    icon="check"
                    onClick={publishProposal}
                    disabled={publishAction.loading}
                  >
                    {publishAction.loading ? 'Publishing…' : 'Publish update'}
                  </Button>
                  <Button variant="secondary" onClick={editProposal}>Edit</Button>
                  <Button variant="ghost" onClick={cancelProposal}>Cancel</Button>
                </div>
              </section>
            )}

            {published && (
              <section className="assistant-published" aria-label="Published update">
                <span className="assistant-published-icon">
                  <Icon name="check" size={22}/>
                </span>
                <div>
                  <strong>Availability is live</strong>
                  <p>
                    {published.proposal.ageGroup}: {published.proposal.nextCount} openings ·{' '}
                    {published.proposal.note}
                  </p>
                  <div>
                    <Button
                      variant="secondary"
                      size="sm"
                      icon="search"
                      onClick={() => go('results', {
                        zip: '19130',
                        childrenAges: [published.proposal.ageGroup],
                        schedule: published.proposal.schedule === 'Part time'
                          ? 'Part time'
                          : 'Full time',
                      })}
                    >
                      See what families see
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => go('profile', { providerId: PROVIDER_ID })}
                    >
                      Open profile
                    </Button>
                  </div>
                </div>
              </section>
            )}

            {postDraft && (
              <section className="assistant-post-review" aria-label="Profile post review">
                <div className="assistant-review-heading">
                  <span>
                    <Icon name="image" size={16}/>
                  </span>
                  <div>
                    <strong>Review profile post</strong>
                    <p>Provider supplied. You can edit every word before publishing.</p>
                  </div>
                </div>

                <img
                  className="assistant-post-preview"
                  src={postDraft.image}
                  alt={postDraft.altText}
                />

                <label className="assistant-post-field">
                  <span>Caption</span>
                  <textarea
                    rows={4}
                    value={postDraft.caption}
                    onChange={(event) =>
                      setPostDraft((current) => ({
                        ...current,
                        caption: event.target.value,
                      }))}
                  />
                </label>

                <label className="assistant-post-field">
                  <span>Image description</span>
                  <input
                    value={postDraft.altText}
                    onChange={(event) =>
                      setPostDraft((current) => ({
                        ...current,
                        altText: event.target.value,
                      }))}
                  />
                  <small>Used by screen readers and shown if the image cannot load.</small>
                </label>

                <label className="assistant-consent">
                  <input
                    type="checkbox"
                    checked={photoConsent}
                    onChange={(event) => setPhotoConsent(event.target.checked)}
                  />
                  <span>
                    I confirm our center has permission to publish this photo.
                  </span>
                </label>

                {publishPostAction.error && (
                  <p className="assistant-review-error" role="alert">
                    Couldn’t publish this post: {publishPostAction.error.message}
                  </p>
                )}

                <div className="assistant-review-actions">
                  <Button
                    variant="primary"
                    icon="check"
                    onClick={publishPost}
                    disabled={
                      !photoConsent ||
                      !postDraft.caption.trim() ||
                      !postDraft.altText.trim() ||
                      publishPostAction.loading
                    }
                  >
                    {publishPostAction.loading ? 'Publishing…' : 'Publish post'}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() =>
                      setPostDraft((current) => ({
                        ...current,
                        caption:
                          'Today’s garden time gave our learners room to explore, notice, and grow together.',
                      }))}
                  >
                    Make shorter
                  </Button>
                  <Button variant="ghost" onClick={cancelPost}>Cancel</Button>
                </div>
              </section>
            )}

            {publishedPost && (
              <section className="assistant-published" aria-label="Published profile post">
                <span className="assistant-published-icon">
                  <Icon name="check" size={22}/>
                </span>
                <div>
                  <strong>Profile post is live</strong>
                  <p>Your approved photo and caption now appear on Sunnyside’s public profile.</p>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon="image"
                    onClick={() => go('profile', { providerId: PROVIDER_ID })}
                  >
                    View published post
                  </Button>
                </div>
              </section>
            )}

            {tourProposal && (
              <section className="assistant-tour-review" aria-label="Tour confirmation review">
                <div className="assistant-review-heading">
                  <span>
                    <Icon
                      name={tourProposal.tourType === 'virtual' ? 'video' : 'calendar-check'}
                      size={16}
                    />
                  </span>
                  <div>
                    <strong>Review tour request</strong>
                    <p>The family is waiting for the center to confirm.</p>
                  </div>
                </div>

                <dl>
                  <div>
                    <dt>Family</dt>
                    <dd>{tourProposal.parentName}</dd>
                  </div>
                  <div>
                    <dt>Age group</dt>
                    <dd>{tourProposal.childAge}</dd>
                  </div>
                  <div>
                    <dt>Visit</dt>
                    <dd>{tourProposal.tourType === 'virtual' ? 'Virtual tour' : 'In-person tour'}</dd>
                  </div>
                  <div>
                    <dt>When</dt>
                    <dd>{tourProposal.date} at {tourProposal.time}</dd>
                  </div>
                </dl>

                {confirmTourAction.error && (
                  <p className="assistant-review-error" role="alert">
                    Couldn’t confirm this tour: {confirmTourAction.error.message}
                  </p>
                )}

                <div className="assistant-review-actions">
                  <Button
                    variant="primary"
                    icon="check"
                    onClick={confirmTour}
                    disabled={confirmTourAction.loading}
                  >
                    {confirmTourAction.loading ? 'Confirming…' : 'Confirm tour'}
                  </Button>
                  <Button variant="ghost" onClick={cancelTourConfirmation}>Cancel</Button>
                </div>
              </section>
            )}

            {confirmedTour && (
              <section className="assistant-published" aria-label="Confirmed tour">
                <span className="assistant-published-icon">
                  <Icon name="calendar-check" size={21}/>
                </span>
                <div>
                  <strong>Tour confirmed</strong>
                  <p>
                    {confirmedTour.parentName} · {confirmedTour.date} at {confirmedTour.time}
                  </p>
                  <span className="assistant-confirmation-note">
                    The family confirmation text is simulated in this prototype.
                  </span>
                </div>
              </section>
            )}

            {waitlistProposal && (
              <section className="assistant-tour-review" aria-label="Waitlist entry review">
                <div className="assistant-review-heading">
                  <span><Icon name="inbox" size={16}/></span>
                  <div>
                    <strong>Review waitlist entry</strong>
                    <p>Confirm the family details before adding them.</p>
                  </div>
                </div>
                <dl>
                  <div><dt>Family</dt><dd>{waitlistProposal.parentName}</dd></div>
                  <div><dt>Age group</dt><dd>{waitlistProposal.childAge}</dd></div>
                  <div><dt>Phone</dt><dd>{waitlistProposal.phone}</dd></div>
                  <div><dt>Status</dt><dd>Waiting</dd></div>
                </dl>
                {addWaitlistAction.error && (
                  <p className="assistant-review-error" role="alert">
                    Couldn’t add this family: {addWaitlistAction.error.message}
                  </p>
                )}
                <div className="assistant-review-actions">
                  <Button variant="primary" icon="check" onClick={addToWaitlist}>
                    Add to waitlist
                  </Button>
                  <Button variant="ghost" onClick={cancelWaitlist}>Cancel</Button>
                </div>
              </section>
            )}

            {addedWaitlistEntry && (
              <section className="assistant-published" aria-label="Added waitlist entry">
                <span className="assistant-published-icon"><Icon name="check" size={21}/></span>
                <div>
                  <strong>Family added to the waitlist</strong>
                  <p>{addedWaitlistEntry.parentName} · {addedWaitlistEntry.childAge}</p>
                </div>
              </section>
            )}

            {freshnessConfirmed && (
              <section className="assistant-published" aria-label="Availability confirmed">
                <span className="assistant-published-icon"><Icon name="check" size={21}/></span>
                <div>
                  <strong>Availability confirmed</strong>
                  <p>Sunnyside now shows “updated just now” to families.</p>
                </div>
              </section>
            )}

            {noticeProposal && (
              <section className="assistant-tour-review" aria-label="Special hours review">
                <div className="assistant-review-heading">
                  <span><Icon name="bell" size={16}/></span>
                  <div>
                    <strong>Review special hours</strong>
                    <p>This notice will appear on the public profile.</p>
                  </div>
                </div>
                <dl>
                  <div><dt>Date</dt><dd>{noticeProposal.dateLabel}</dd></div>
                  <div><dt>Hours</dt><dd>{noticeProposal.message}</dd></div>
                </dl>
                {publishNoticeAction.error && (
                  <p className="assistant-review-error" role="alert">
                    Couldn’t publish this notice: {publishNoticeAction.error.message}
                  </p>
                )}
                <div className="assistant-review-actions">
                  <Button variant="primary" icon="check" onClick={publishNotice}>
                    Publish notice
                  </Button>
                  <Button variant="ghost" onClick={cancelNotice}>Cancel</Button>
                </div>
              </section>
            )}

            {publishedNotice && (
              <section className="assistant-published" aria-label="Published special hours">
                <span className="assistant-published-icon"><Icon name="bell" size={21}/></span>
                <div>
                  <strong>Special hours are live</strong>
                  <p>{publishedNotice.dateLabel} · {publishedNotice.message}</p>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => go('profile', { providerId: PROVIDER_ID })}
                  >
                    View public profile
                  </Button>
                </div>
              </section>
            )}

            {waitlistContactProposal && (
              <section className="assistant-contact-review" aria-label="Waitlist contact review">
                <div className="assistant-review-heading">
                  <span><Icon name="phone" size={16}/></span>
                  <div>
                    <strong>Review family message</strong>
                    <p>The family’s contact information stays private.</p>
                  </div>
                </div>
                <dl>
                  <div><dt>Family</dt><dd>{waitlistContactProposal.parentName}</dd></div>
                  <div><dt>Age group</dt><dd>{waitlistContactProposal.childAge}</dd></div>
                  <div><dt>Phone</dt><dd>{waitlistContactProposal.phone}</dd></div>
                  <div>
                    <dt>Joined</dt>
                    <dd>
                      {new Intl.DateTimeFormat('en-US', {
                        month: 'short',
                        day: 'numeric',
                      }).format(new Date(waitlistContactProposal.joinedAt))}
                    </dd>
                  </div>
                </dl>
                <label className="assistant-post-field">
                  <span>Message</span>
                  <textarea
                    rows={4}
                    value={waitlistContactMessage}
                    onChange={(event) => setWaitlistContactMessage(event.target.value)}
                  />
                </label>
                {contactWaitlistAction.error && (
                  <p className="assistant-review-error" role="alert">
                    Couldn’t contact this family: {contactWaitlistAction.error.message}
                  </p>
                )}
                <div className="assistant-review-actions">
                  <Button
                    variant="primary"
                    icon="check"
                    onClick={contactWaitlistFamily}
                    disabled={!waitlistContactMessage.trim() || contactWaitlistAction.loading}
                  >
                    {contactWaitlistAction.loading ? 'Sending…' : 'Approve message'}
                  </Button>
                  <Button variant="ghost" onClick={cancelWaitlistContact}>Cancel</Button>
                </div>
              </section>
            )}

            {contactedWaitlistEntry && (
              <section className="assistant-published" aria-label="Waitlist family contacted">
                <span className="assistant-published-icon"><Icon name="check" size={21}/></span>
                <div>
                  <strong>Family marked as contacted</strong>
                  <p>{contactedWaitlistEntry.parentName} · {contactedWaitlistEntry.childAge}</p>
                  <span className="assistant-confirmation-note">
                    The outgoing text is simulated in this prototype.
                  </span>
                </div>
              </section>
            )}

            {placementProposal && (
              <section className="assistant-tour-review" aria-label="Waitlist placement review">
                <div className="assistant-review-heading">
                  <span><Icon name="check-circle" size={16}/></span>
                  <div>
                    <strong>Review placement</strong>
                    <p>This removes the family from the active waitlist.</p>
                  </div>
                </div>
                <dl>
                  <div><dt>Family</dt><dd>{placementProposal.parentName}</dd></div>
                  <div><dt>Age group</dt><dd>{placementProposal.childAge}</dd></div>
                  <div><dt>Current status</dt><dd>Contacted</dd></div>
                  <div><dt>New status</dt><dd>Placed</dd></div>
                </dl>
                {placeWaitlistAction.error && (
                  <p className="assistant-review-error" role="alert">
                    Couldn’t mark this family as placed: {placeWaitlistAction.error.message}
                  </p>
                )}
                <div className="assistant-review-actions">
                  <Button
                    variant="primary"
                    icon="check"
                    onClick={placeWaitlistFamily}
                    disabled={placeWaitlistAction.loading}
                  >
                    {placeWaitlistAction.loading ? 'Saving…' : 'Mark as placed'}
                  </Button>
                  <Button variant="ghost" onClick={cancelPlacement}>Cancel</Button>
                </div>
              </section>
            )}

            {placedWaitlistEntry && (
              <section className="assistant-published" aria-label="Waitlist family placed">
                <span className="assistant-published-icon"><Icon name="check-circle" size={21}/></span>
                <div>
                  <strong>Family placed</strong>
                  <p>{placedWaitlistEntry.parentName} · {placedWaitlistEntry.childAge}</p>
                </div>
              </section>
            )}

            <div ref={transcriptEnd}/>
          </div>

          {!proposal &&
            !postDraft &&
            !tourProposal &&
            !waitlistProposal &&
            !noticeProposal &&
            !waitlistContactProposal &&
            !placementProposal && (
            <QuickReplies missingField={missingField} onReply={submitMessage}/>
          )}
          {waitlistDraft.parentName && !waitlistDraft.phone && (
            <div className="assistant-quick-replies" aria-label="Suggested phone number">
              <button type="button" onClick={() => submitMessage('(215) 555-0188')}>
                (215) 555-0188
              </button>
            </div>
          )}

          <form
            className="assistant-composer"
            onSubmit={(event) => {
              event.preventDefault();
              submitMessage(input);
            }}
          >
            <label htmlFor="assistant-message" className="sr-only">
              Message your provider assistant
            </label>
            <input
              ref={fileInput}
              className="sr-only"
              type="file"
              aria-label="Photo to publish"
              accept="image/*"
              onChange={handleAttachment}
            />
            <button
              className="assistant-attach"
              type="button"
              aria-label="Attach a photo"
              onClick={() => fileInput.current?.click()}
              disabled={loading || Boolean(error)}
            >
              <Icon name="image" size={18}/>
            </button>
            <textarea
              id="assistant-message"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  if (canSend) submitMessage(input);
                }
              }}
              rows={1}
              placeholder={loading ? 'Loading center details…' : 'Tell me what changed…'}
              disabled={loading || Boolean(error)}
            />
            <button type="submit" disabled={!canSend} aria-label="Send message">
              <Icon name="arrow-right" size={18}/>
            </button>
          </form>
          {attachmentError && (
            <p className="assistant-attachment-error" role="alert">{attachmentError}</p>
          )}
          <p className="assistant-composer-note">
            Prototype conversation. Every public change requires your approval.
          </p>
        </section>

        <AvailabilityContext
          provider={provider}
          bookings={bookings}
          waitlistEntries={waitlistEntries}
          notices={notices}
        />
      </main>
    </div>
  );
};
